import {observable, computed, makeObservable} from "mobx";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {WebsiteModuleDisplayModel} from "./website-module.display.model";
import {WebsiteModulePageMetadataViewModel} from "./website-module.page-metadata-view-model";
import {LanguageRepository} from "../../../domain/locale/services/language/language.repository";
import {RouterController} from "../../../core/components/router/router-controller";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {contractsUtils} from "../../../infrastructure/utils/contracts-utils";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {WebsiteModuleConfig} from "../website-module.config";
import {UserSelectionModel} from "../../../core/models/user-selection.model";
import {USER_SELECTIONS_MANAGER_ACTIONS, UserSelectionsManager} from "../../../core/managers/user-selections.manager";
import {singleton} from "../../../infrastructure/decorators/singleton";
import {WebsiteModuleEntitiesContext} from "./website-module.entities-context";
import {makeObservableClass} from "../../../infrastructure/decorators/observable";
import {PAGES, ROUTE_NAMES} from "../../../core/consts";
import {COMPETITION_CONTAINER} from "../../../containers/competition-container/competition-container.consts";
import {UserAgentViewModel} from "../../../core/models/user-agent.view-model";
import {PartialGameViewModel} from "../../../domain/partial-game/models/partial-game-view-model";
import {CompetitorViewModel} from "../../../domain/common/competitor/models/competitor-view-model";
import {CompetitionViewModel} from "../../../domain/common/competition/models/competition-view-model";
import {BreadcrumbsManager} from "../../../core/managers/breadcrumbs/breadcrumbs-manager";
import {BreadcrumbsItemModel} from "../../../core/managers/breadcrumbs/breadcrumbs.item-model";
import {RemoteConfigManager} from "../../../third-party-services/remote-config/remote-config-manager";
import {DBA} from "../../../core/app/consts";
import {WebToAppCTAModel} from "../../../core/models/web-to-app-cta/web-to-app-cta.model";
import type {BolaoSettingsDataSource} from "../../../domain/bolao-settings/models/bolao-settings-data.source";
import type {StoryDomainModel} from "../../../domain/full-game/models/story/story.domain-model";
import {PopupsDisplayManager} from "../../../core/managers/popups-display.manager";
import {AD_CONFIG} from "../consts/ads-config";
import {EXTERNAL_AD_CONFIG} from "../consts/external-ad-config";
import {GameCenterLinkParams} from "../../../containers/game-center-container/models/game-center-link-params";

@makeObservableClass
@singleton
export class WebsiteModuleState extends BaseModuleState {
  @observable _isMainScreenRendered: Boolean = false;
  @observable lastUserSelection: UserSelectionModel = null;
  @observable isUSPopupVisible: Boolean = false;
  @observable displaySettings: WebsiteModuleDisplayModel;
  @observable entitiesContext: WebsiteModuleEntitiesContext;
  @observable isBottomNavigationVisible: Boolean = true;
  @observable showFloatingButton: Boolean = true;
  @observable showWscFloatingButton: Boolean = true;
  @observable shouldDisplayDidomiPopup: Boolean = false;
  @observable isStickyExternalAdVisible: Boolean = false;
  languageRepository: LanguageRepository;
  routerController: RouterController;
  config: WebsiteModuleConfig;
  breadcrumbsManager: BreadcrumbsManager;
  webToAppCTAModel: WebToAppCTAModel = new WebToAppCTAModel();

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, WebsiteModuleConfig, LanguageRepository, RouterController, WebsiteModulePageMetadataViewModel, WebsiteModuleEntitiesContext, WebsiteModuleDisplayModel, UserAgentViewModel, UserSelectionsManager, BreadcrumbsManager, RemoteConfigManager, PopupsDisplayManager];

  constructor(
    navigationManager: NavigationManager,
    appStore: AppStore,
    logger: LoggerUtils,
    datetimeUtils: DatetimeUtils,
    device: DeviceViewModel,
    termsUtils: TermsUtils,
    config: WebsiteModuleConfig,
    languageRepository: LanguageRepository,
    routerController: RouterController,
    pageMetadata: WebsiteModulePageMetadataViewModel,
    entitiesContext: WebsiteModuleEntitiesContext,
    displaySettings: WebsiteModuleDisplayModel,
    userAgentViewModel: UserAgentViewModel,
    userSelectionsManager: UserSelectionsManager,
    breadcrumbsManager: BreadcrumbsManager,
    remoteConfigManager: RemoteConfigManager,
    popupsDisplayManager: PopupsDisplayManager
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    contractsUtils.requiredType(languageRepository, LanguageRepository);
    contractsUtils.requiredType(routerController, RouterController);

    this.languageRepository = languageRepository;
    this.routerController = routerController;
    this.isUSPopupVisible = false;
    this.pageMetadata = pageMetadata;

    this.entitiesContext = entitiesContext;
    this.displaySettings = displaySettings;
    this.userAgentViewModel = userAgentViewModel;
    this.userSelectionsManager = userSelectionsManager;
    this.breadcrumbsManager = breadcrumbsManager;
    this.remoteConfigManager = remoteConfigManager;
    this.popupsDisplayManager = popupsDisplayManager;

    makeObservable(this);
  }

  get isPopulated() {
    return true;
  }

  @computed get breadcrumbsList(): Array<BreadcrumbsItemModel> {
    return this.breadcrumbsManager.list;
  }

  @computed get isMainScreenRendered(): Boolean {
    return this._isMainScreenRendered;
  }

  @computed get shouldRenderDidomiNotice(): Boolean {
    return !this.userAgentViewModel.isPrerender && this.appState.shouldSupportConsent;
  }

  @computed get shouldDisplayStickyExternalAd(): Boolean {
    return this.appState.externalAdsModel.shouldRender;
  }

  @computed get shouldRenderWizard(): Boolean {
    return !this.userAgentViewModel.isPrerender && !this.isUSPopupVisible && this.appState.wizardDisplayModel.isVisible;//TODO check if need change here
  }

  set isMainScreenRendered(isMainScreenRendered: Boolean) {
    this._isMainScreenRendered = isMainScreenRendered;
  }

  @computed get currentRoute() {
    return this.routerController.currentRoute
  }

  @computed get outbrainId(): String {
    let outbrainId: String = null;

    if (this.currentRoute) {
      outbrainId = this.currentRoute.config.outbrainId;
    }

    return outbrainId;
  }

  @computed get userSelectionsToasterText(): String {
    const addedMap = {
      [PartialGameViewModel.name]: this.terms.WEB_CONFIRMATION_TOASTER_GAME_ADDED,
      [CompetitorViewModel.name]: this.terms.WEB_CONFIRMATION_TOASTER_COMPETITOR_ADDED,
      [CompetitionViewModel.name]: this.terms.WEB_CONFIRMATION_TOASTER_COMPETITION_ADDED,
    }

    const removedMap = {
      [PartialGameViewModel.name]: this.terms.WEB_CONFIRMATION_TOASTER_GAME_REMOVED,
      [CompetitorViewModel.name]: this.terms.WEB_CONFIRMATION_TOASTER_COMPETITOR_REMOVED,
      [CompetitionViewModel.name]: this.terms.WEB_CONFIRMATION_TOASTER_COMPETITION_REMOVED,
    }

    const mapper: Object = this.lastUserSelection?.action === USER_SELECTIONS_MANAGER_ACTIONS.add ? addedMap : removedMap;
    const userSelectionsToasterText: String = mapper[this.lastUserSelection?.entity.type];

    return userSelectionsToasterText || '';
  }

  get shouldRenderNotificationApproval(): Boolean {
    const shouldRenderNotificationApproval: Boolean = this.appState.appNotificationDisplayModel.allowNotifications && this.appState.appNotificationDisplayModel.shouldRenderNotificationApproval;

    return shouldRenderNotificationApproval;
  }

  get shouldRenderSecondNoticeMessage(): Boolean {
    const shouldRenderSecondNoticeMessage: Boolean = this.appState.appNotificationDisplayModel.allowNotifications && this.appState.appNotificationDisplayModel.shouldRenderSecondNoticeMessage;

    return shouldRenderSecondNoticeMessage;
  }

  get shouldRenderTopMessage(): Boolean {
    const shouldRenderTopMessage: Boolean = this.appState.appNotificationDisplayModel.allowNotifications && this.shouldRenderNotificationApproval;

    return shouldRenderTopMessage;
  }

  get hasNotificationBar(): Boolean {
    const hasNotificationBar: Boolean = !this.device.isSmallView && this.shouldRenderNotificationApproval;

    return hasNotificationBar;
  }

  get hideNotificationBar(): Boolean {
    const hideNotificationBar: Boolean = !this.device.isSmallView && this.shouldRenderSecondNoticeMessage;

    return hideNotificationBar;
  }

  @computed get shouldRenderPreviewStandings(): Boolean {
    // const hasEntityIds: Boolean = this.appState.standingsPreviewDisplayModel.hasEntityIds;

    const entityPagesNames = [
      ROUTE_NAMES.competitorScores,
      ROUTE_NAMES.competitionScores,
      ROUTE_NAMES.allScores,
      ROUTE_NAMES.myScoresPage
    ];

    const isStandingsTab: Boolean = this.currentLinkModel?.params.filter === COMPETITION_CONTAINER.FILTERS.standings
    const shouldRenderPreviewStandings: Boolean = entityPagesNames.includes(this.currentLinkModel?.name) && !isStandingsTab

    return shouldRenderPreviewStandings;
  }

  @computed get isGamesPredictionsVisible(): Boolean {
    const entityPagesNames = [
      ROUTE_NAMES.competitionScores,
      ROUTE_NAMES.competitionStandings,
      ROUTE_NAMES.competitionBrackets,
      ROUTE_NAMES.competitorScores,
      ROUTE_NAMES.competitorStandings,
      ROUTE_NAMES.competitorBrackets,
      ROUTE_NAMES.allScores,
      ROUTE_NAMES.myScoresPage
    ];

    const visibleByRouteName = entityPagesNames.includes(this.currentLinkModel?.name);

    return visibleByRouteName;
  }

  @computed get isStickyContent(): Boolean {
    const isStickyContent: Boolean = !!this.entitiesContext?.game && !this.device.isSmallView;

    return isStickyContent
  }

  @computed get shouldRenderBottomNavigationBar(): Boolean {
    const shouldRenderBottomNavigationBar: Boolean = this.device.isSmallView;

    return shouldRenderBottomNavigationBar;
  }

  @computed get shouldRenderDescriptionHeader(): Boolean {
    const validRoutes: Array<ROUTE_NAMES> = [
      ROUTE_NAMES.competitionScores,
      ROUTE_NAMES.competitionStandings,
      ROUTE_NAMES.competitionBrackets,
      ROUTE_NAMES.competitorScores,
      ROUTE_NAMES.competitorStandings,
      ROUTE_NAMES.competitorBrackets,
      ROUTE_NAMES.game,
      ROUTE_NAMES.following,
      ROUTE_NAMES.catalog
    ]

    const shouldRenderDescriptionHeader: Boolean = this.device.isSmallView && validRoutes.includes(this.currentLinkModel?.name)

    return shouldRenderDescriptionHeader;
  }

  @computed get isCompetitorContext(): Boolean {
    const validRoutes: Array<ROUTE_NAMES> = [
      ROUTE_NAMES.competitorScores,
      ROUTE_NAMES.competitorStandings,
      ROUTE_NAMES.competitorBrackets,
    ];

    const isCompetitorContext: Boolean = validRoutes.includes(this.currentLinkModel?.name)

    return isCompetitorContext;
  }

  @computed get isCompetitionContext(): Boolean {
    const validRoutes: Array<ROUTE_NAMES> = [
      ROUTE_NAMES.competitionScores,
      ROUTE_NAMES.competitionStandings,
      ROUTE_NAMES.competitionBrackets,
    ];

    const isCompetitionContext: Boolean = validRoutes.includes(this.currentLinkModel?.name)

    return isCompetitionContext;
  }

  @computed get onEditMode(): Boolean {
    return this.appState.followingDisplayModel.onEditMode
  }

  @computed get shouldRenderEditButton(): Boolean {
    const isFollowingRoute: Boolean = ROUTE_NAMES.following === this.currentLinkModel?.name;

    const numTeams = this.userSelectionsManager.competitorsIds?.length;
    const numLeagues = this.userSelectionsManager.competitionsIds?.length;
    const hasUserSelections: Boolean = (numTeams + numLeagues) > 0

    return isFollowingRoute && hasUserSelections
  }

  @computed get editButtonText(): Boolean {
    return this.onEditMode ? this.terms.WEB_WIZARD_DONE : this.terms.WEB_FOLLOWING_EDIT_BUTTON
  }

  @computed get isBettingPromotionVisible(): Boolean {
    return this.appStore.state.appBettingPromotionDisplayModel.isVisible;
  }

  @computed get contextGameId(): Number {
    return this.appState.context.game?.id;
  }

  @computed get currentGameId(): Number {
    return this.currentLinkModel?.name === ROUTE_NAMES.game && this.currentLinkModel?.hashParams.id
  }
  @computed get currentMatchupId(): Number {
    const gameCenterParams = new GameCenterLinkParams(this.currentLinkModel?.params?.competitionKey, this.currentLinkModel?.params?.matchDetails, this.currentLinkModel?.params?.sportName);

    return gameCenterParams.matchupId
  }

  @computed get bolaoSettings(): BolaoSettingsDataSource {
    return this.appState.bolaoSettings;
  }

  @computed get shouldRenderFloatingButton(): Boolean {
    const shouldRenderFloatingButton: Boolean = this.appState.appBolaoDisplayModel.isFloatingVisible && this.showFloatingButton && this.device.isSmallView && !this.shouldRenderWscFloatingButton;

    return shouldRenderFloatingButton;
  }

  @computed get wscFloatingStory(): StoryDomainModel {
    return this.appState.wscFloatingStory;
  }

  @computed get shouldRenderWscFloatingButton(): Boolean {
    return this.appState.shouldRenderWscFloatingButton && this.showWscFloatingButton;
  }

  @computed get shouldRenderEntityDescription(): Number {
    return this.pageMetadata?.dataSchemaFAQ && this.appState.shouldRenderEntityDescription;
  }

  @computed get shouldRenderDBAIframe(): Boolean {
    const isUserCountryIdValid: Boolean = this.appState.locale.userCountryId === 21;
    const shouldRenderDBAIframe: Boolean = !this.appState.settings.isWidget && isUserCountryIdValid;

    return shouldRenderDBAIframe;
  }

  @computed get isGameRoute(): Boolean {
    const isGameRoute: Boolean = ROUTE_NAMES.game === this.currentLinkModel?.name;

    return isGameRoute;
  }

  @computed get isGameCenter(): Number {
    const isGameCenter: Boolean = this.device.isSmallView && this.isGameRoute

    return isGameCenter;
  }

  @computed get isGameContext(): Number {
    const validRoutes: Array<ROUTE_NAMES> = [ROUTE_NAMES.game]

    const isGameContext: Boolean = validRoutes.includes(this.currentLinkModel?.name)

    return isGameContext;
  }

  @computed get hasStripLinks() {
    return this.appState.importantInfoStripDisplay && this.appState.hasStripLinks && this.isAllScores
  }

  get isStoreDomain(): Boolean {
    return this.appState.isStoreDomain;
  }

  @computed get userCountryId() {
    return this.appState.locale?.userCountryId;
  }

  @computed get shouldRenderDbaSmallBanner(): Boolean {
    const isStandingsTab: Boolean = this.currentLinkModel?.params.filter === COMPETITION_CONTAINER.FILTERS.standings
    const shouldRenderDbaSmallBanner: Boolean = this.device.isSmallView && (this.isCompetitor || this.isCompetition) && !isStandingsTab

    return shouldRenderDbaSmallBanner;
  }

  @computed get shouldRenderAdSmallBanner(): Boolean {
    const shouldRenderAdSmallBanner: Boolean = this.device.isSmallView && (this.isCompetitor || this.isCompetition);

    return shouldRenderAdSmallBanner;
  }

  @computed get shouldRenderDbaLargeBanner(): Boolean {
    const shouldRenderDbaLargeBanner: Boolean = !this.device.isSmallView;

    return shouldRenderDbaLargeBanner;
  }

  @computed get webPage(): DBA.PAGE {
    let webPage: Boolean;

    if (this.isCompetitor) {
      webPage = DBA.PAGE.competitor;
    } else if (this.isCompetition) {
      webPage = DBA.PAGE.competition;
    } else if (this.isGameCenter) {
      webPage = DBA.PAGE.match;
    } else if (this.isAthlete) {
      webPage = DBA.PAGE.athlete;
    } else if (this.isMyScores) {
      webPage = DBA.PAGE.myScore;
      if (this.appState.isLive) {
        webPage = DBA.PAGE.live;
      }
    } else if (this.isAllScores) {
      webPage = DBA.PAGE.allScores;
      if (this.appState.isLive) {
        webPage = DBA.PAGE.live;
      }
    }

    return webPage;
  }

  @computed get isCompetitor(): Boolean {
    const validRoutes: Array<ROUTE_NAMES> = [
      ROUTE_NAMES.competitorScores,
      ROUTE_NAMES.competitorStandings,
      ROUTE_NAMES.competitorBrackets,
    ];

    const isCompetitorContext: Boolean = validRoutes.includes(this.currentLinkModel?.name)

    return isCompetitorContext;
  }

  @computed get isCompetition(): Boolean {
    const validRoutes: Array<ROUTE_NAMES> = [
      ROUTE_NAMES.competitionScores,
      ROUTE_NAMES.competitionStandings,
      ROUTE_NAMES.competitionBrackets,
    ];

    const isCompetitionContext: Boolean = validRoutes.includes(this.currentLinkModel?.name)

    return isCompetitionContext;
  }

  @computed get isAllScores(): Boolean {
    return this.currentLinkModel && this.currentLinkModel.name === ROUTE_NAMES.allScores;
  }

  @computed get isMyScores(): Boolean {
    return this.currentLinkModel && this.currentLinkModel.name === ROUTE_NAMES.myScoresPage;
  }

  @computed get isAthlete(): Boolean {
    return this.currentLinkModel && this.currentLinkModel.name === ROUTE_NAMES.athlete;
  }

  @computed get sportTypeId(): Number {
    return this.appState.context.sportType?.id;
  }

  @computed get externalAdConfig() {
    let externalAdConfig: EXTERNAL_AD_CONFIG = null;
    if (this.isCompetitorContext) {
      externalAdConfig = this.shouldRenderAdSmallBanner ? EXTERNAL_AD_CONFIG.MOBILE.COMPETITOR.AD320X100 : EXTERNAL_AD_CONFIG.DESKTOP.COMPETITOR.AD970X90;
    } else if (this.isCompetitionContext) {
      externalAdConfig = this.shouldRenderAdSmallBanner ? EXTERNAL_AD_CONFIG.MOBILE.COMPETITION.AD320X100 : EXTERNAL_AD_CONFIG.DESKTOP.COMPETITION.AD970X90;
    } else if (this.isGameContext && !this.device.isSmallView) {
      externalAdConfig = EXTERNAL_AD_CONFIG.DESKTOP.MATCH.AD970X90;
    } else if (this.isAthlete && !this.device.isSmallView) {
      externalAdConfig = EXTERNAL_AD_CONFIG.DESKTOP.ATHLETE.AD970X90;
    } else if ((this.isAllScores || this.isMyScores) && !this.device.isSmallView) {
      externalAdConfig = EXTERNAL_AD_CONFIG.DESKTOP.HP.AD970X90;
    }

    return externalAdConfig;
  }

  @computed get shouldRenderAd(): Boolean {
    return this.appState.externalAdsModel.shouldRender && (this.appState.externalAdsModel.remoteConfigModel.adsPrioritize || !this.shouldRenderDBAIframe);
  }

  @computed get dbaConfig() {
    let dbaConfig = this.shouldRenderDbaLargeBanner ? AD_CONFIG.AD728X120 : (this.shouldRenderDbaSmallBanner ? AD_CONFIG.AD320X80 : null);

    return dbaConfig;
  }

  @computed get adConfig() {
    let adConfig = this.shouldRenderDbaLargeBanner ? AD_CONFIG.AD728X120 : (this.shouldRenderDbaSmallBanner ? AD_CONFIG.AD320X80 : null);
    if (this.shouldRenderAd) {
      adConfig = this.externalAdConfig;
    }

    return adConfig;
  }

  @computed get isBettingPage(): Boolean {
    const bettingPages: Array<ROUTE_NAMES> = [ROUTE_NAMES.betting, ...(Object.values(PAGES.BETTING))];

    return bettingPages.includes(this.currentLinkModel?.name);
  }

  @computed get isCompetitionPage(): Boolean {
    return [ROUTE_NAMES.competition, Object.values(PAGES.COMPETITION)].flat()?.includes((this.currentLinkModel?.name))
  }

  @computed get isCompetitorPage(): Boolean {
    return [ROUTE_NAMES.competitor, Object.values(PAGES.COMPETITOR)].flat()?.includes((this.currentLinkModel?.name))
  }

  @computed get shouldRenderAdsBorders(): Boolean {
    return this.appState.shouldRenderAdsBorders
  }
}
